import React from "react";
import { Button } from "@/components/ui/button";
import { LogOut, ShieldCheck, User, Globe } from "lucide-react";
import { useAuth } from "@/components/shared/AuthContext";
import { Pill } from "@/components/shared/Pill";
import { useTranslation } from "@/components/shared/i18n";

export function Layout({ title, children }) {
  const { me, logout } = useAuth();
  const { lang, setLang, t } = useTranslation();

  return (
    <div data-testid="app-shell" className="min-h-screen bg-[#060A14] text-slate-50 w-full overflow-x-hidden">
      <div
        className="absolute inset-x-0 top-0 h-[380px] bg-[radial-gradient(ellipse_at_top,rgba(56,189,248,0.28),transparent_60%),radial-gradient(ellipse_at_top_left,rgba(125,211,252,0.18),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgba(59,130,246,0.12),transparent_55%)]"
        aria-hidden
      />
      <header
        data-testid="app-header"
        className="sticky top-0 z-20 border-b border-white/10 bg-[#0b0d12]/80 backdrop-blur"
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div
              data-testid="brand-mark"
              className="flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-xl border border-white/10 bg-white/[0.08]"
            >
              <span className="text-sm sm:text-lg font-semibold">BM</span>
            </div>
            <div>
              <div data-testid="brand-title" className="text-xs sm:text-sm text-slate-200">
                Blanc des Montagnes
              </div>
              <div data-testid="page-title" className="text-sm sm:text-base font-semibold">
                {title}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              data-testid="lang-toggle"
              onClick={() => setLang(lang === "fr" ? "en" : "fr")}
              className="flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.08] px-2 py-1 text-xs font-medium text-slate-200 hover:bg-white/10 transition-colors"
              title={lang === "fr" ? "Switch to English" : "Passer en français"}
            >
              <Globe className="h-3.5 w-3.5" />
              <span className="uppercase">{lang === "fr" ? "EN" : "FR"}</span>
            </button>
            {me?.role === "admin" ? (
              <Pill testId="role-pill" tone="violet">
                <span className="inline-flex items-center gap-1 sm:gap-1.5">
                  <ShieldCheck className="h-3 w-3 sm:h-3.5 sm:w-3.5" /> <span className="hidden sm:inline">{t("layout.admin")}</span>
                </span>
              </Pill>
            ) : (
              <Pill testId="role-pill" tone="emerald">
                <span className="inline-flex items-center gap-1 sm:gap-1.5">
                  <User className="h-3 w-3 sm:h-3.5 sm:w-3.5" /> <span className="hidden sm:inline">{t("layout.owner")}</span>
                </span>
              </Pill>
            )}
            <Button
              data-testid="logout-button"
              variant="outline"
              size="sm"
              className="border-white/10 bg-white/[0.08] text-slate-100 hover:bg-white/10 px-2 sm:px-3"
              onClick={logout}
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline ml-1">{t("layout.logout")}</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="relative mx-auto max-w-6xl px-3 sm:px-6 py-4 sm:py-8 w-full">{children}</main>
    </div>
  );
}
